import api from '@/core/api/api';

/**
 * Normalize a lookup row from the API into consistent cache shape.
 * Spreads raw item first, then applies computed fields so empty DynamoDB
 * display strings cannot overwrite fallbacks.
 */
function normalizeLookupItem(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const value = item.value || '';
  const rawDisplay = typeof item.display === 'string' ? item.display.trim() : '';
  const display = rawDisplay || value;

  return {
    ...item,
    value,
    display,
    label: display,
    description: item.description || item.desc || '',
    order: item.order ?? 0,
    active: item.active !== false,
    color: item.color || '',
    color_hsl: item.color_hsl || '',
    color_rgb: item.color_rgb || '',
    associations: item.associations || {},
  };
}

/**
 * Simple Lookup Service
 * Gets lookup values by lookup_type with caching
 */
class LookupService {
  constructor() {
    // Enhanced cache with session-based persistence (no time expiration)
    // Cache Policy: Data persists for the entire user session until:
    // 1. User logs out (manual)
    // 2. Session timeout due to inactivity (forced signoff)
    // 3. Manual cache clearing (for development/debugging)
    this.cache = new Map();
    this.loadingPromises = new Map(); // Prevent duplicate requests
    this.isInitialized = false; // Track if all lookups have been loaded
    this.initializationPromise = null; // Track initialization status
  }

  // Enhanced cache management
  _getCacheKey(lookupType, params = {}) {
    return `${lookupType}_${JSON.stringify(params)}`;
  }

  // Cache is always valid until manually cleared (no time expiration)
  _isCacheValid(cacheEntry) {
    return cacheEntry && cacheEntry.data;
  }

  _setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now() // Keep timestamp for debugging/monitoring only
    });
  }

  _getFromCache(key) {
    const entry = this.cache.get(key);
    return this._isCacheValid(entry) ? entry.data : null;
  }

  // Generic fetch with performance optimizations
  async _fetchLookup(lookupType, params = {}) {
    const cacheKey = this._getCacheKey(lookupType, params);
    
    // Return cached data if valid
    const cachedData = this._getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Return existing promise if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Create new loading promise
    const loadingPromise = this._performFetch(lookupType, params, cacheKey);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const result = await loadingPromise;
      return result;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  async _performFetch(lookupType, params, cacheKey) {
    try {
      const response = await api.get(`/fetch/lookups`, { params: { lookup_type: lookupType, ...params } });
      
      // Handle error response from backend
      if (response.data && response.data.error) {
        console.error(`Lookup failed: ${lookupType} - ${response.data.error}`);
        throw new Error(`Lookup failed: ${lookupType} - ${response.data.error}`);
      }
      
      // Handle successful response - NEW: uniform format with associations
      if (response.data && Array.isArray(response.data.values)) {
        const formattedData = response.data.values
          .map(normalizeLookupItem)
          .filter(Boolean);
        
        // NEW: Cache both formatted data and association metadata for special lookups
        if (lookupType === 'jurisdictions' && response.data.association_metadata?.has_associations) {
          // For jurisdictions, also cache the full uniform response for backward compatibility
          const fullResponse = {
            ...response.data,
            values: formattedData,
            hierarchical: true // Maintain backward compatibility flag
          };
          this._setCache(`${cacheKey}_full`, fullResponse);
        }
        
        // Cache the formatted result
        this._setCache(cacheKey, formattedData);
        
        return formattedData;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error(`Error fetching ${lookupType}:`, error);
      throw error;
    }
  }

  // Batch loading for multiple lookup types
  async batchFetch(lookupTypes) {
    try {
      const promises = lookupTypes.map(type => 
        this._fetchLookup(type).catch(error => {
          console.error(`Batch fetch error for ${type}:`, error);
          throw error; // Re-throw the error instead of using fallback
        })
      );
      
      const results = await Promise.allSettled(promises);
      
      return lookupTypes.reduce((acc, type, index) => {
        const result = results[index];
        if (result.status === 'rejected') {
          console.error(`Lookup ${type} failed:`, result.reason);
          throw result.reason; // Throw error if any lookup fails
        }
        acc[type] = result.value;
        return acc;
      }, {});
    } catch (error) {
      console.error('Batch fetch error:', error);
      throw error;
    }
  }

  // Preload ALL registration and common lookups
  async preloadRegistrationLookups() {
    const registrationTypes = [
      // Basic Info
      'countries',
      'states',
      'referral_source',
      
      // Manager Info
      'admin_departments', 
      'job_titles',
      'experience_levels',
      'jurisdictions',
      
      // NEW: Association lookup types for regulatory data
      'regulatory_bodies',
      'license_types',
      
      // NEW: Asset type and strategy focus associations
      'asset_type',
      'strategy_focus',
      
      // Investor Info - HNWI
      'investment_strategies',
      'risk_tolerances',  
      'time_horizons',
      'net_worth_range',
      'annual_income_range',
      'communication_preference',
      
      // Investor Info - Institutional
      'institution_types',
      'aum_range',
      'investment_ranges',
      'accreditation_types',
      'liquidity_preference',
      'decision_process',
      
      // Admin Info
      'admin_departments',
      'admin_roles',
      'user_roles',
      
      // Common lookups
      'currencies',
      'investment_experience',
      
      // Fund management lookups
      'investment_status',
      'fund_structure',
      'redemption_frequencies',
      'notice_period_requirements'
    ];

    try {
      console.log('Preloading registration lookups...');
      const results = await this.batchFetch(registrationTypes);
      
      // Enhanced logging to show what was actually loaded
      console.log('Registration lookups preloaded:', Object.keys(results));
      console.log('Lookup details:');
      Object.entries(results).forEach(([type, data]) => {
        const count = Array.isArray(data) ? data.length : 0;
        const status = count > 0 ? '✓' : '✗';
        console.log(`  ${status} ${type}: ${count} items`);
        
        // Log first few items for debugging (but limit to avoid console spam)
        if (count > 0 && count <= 3) {
          data.forEach(item => {
            console.log(`    - ${item.value}: ${item.display || item.label}`);
          });
        } else if (count > 3) {
          console.log(`    - ${data[0].value}: ${data[0].display || data[0].label}`);
          console.log(`    - ${data[1].value}: ${data[1].display || data[1].label}`);
          console.log(`    - ... and ${count - 2} more`);
        }
      });
      
      return results;
    } catch (error) {
      console.warn('Registration preload failed, lookups will load on demand:', error);
      return {};
    }
  }

  // Legacy method for backward compatibility
  async preloadCommonLookups() {
    return this.preloadRegistrationLookups();
  }

  // Enhanced fallback data with more comprehensive options
  _getFallbackData(lookupType) {
    const fallbacks = {
      countries: [
        { value: 'US', label: 'United States', display: 'United States' },
        { value: 'CA', label: 'Canada', display: 'Canada' },
        { value: 'GB', label: 'United Kingdom', display: 'United Kingdom' },
        { value: 'DE', label: 'Germany', display: 'Germany' },
        { value: 'FR', label: 'France', display: 'France' },
        { value: 'JP', label: 'Japan', display: 'Japan' },
        { value: 'AU', label: 'Australia', display: 'Australia' },
        { value: 'SG', label: 'Singapore', display: 'Singapore' },
        { value: 'HK', label: 'Hong Kong', display: 'Hong Kong' },
        { value: 'CH', label: 'Switzerland', display: 'Switzerland' }
      ],
      investment_strategies: [
        { value: 'long-short-equity', label: 'Long/Short Equity', display: 'Long/Short Equity' },
        { value: 'market-neutral', label: 'Market Neutral', display: 'Market Neutral' },
        { value: 'event-driven', label: 'Event Driven', display: 'Event Driven' },
        { value: 'macro', label: 'Global Macro', display: 'Global Macro' },
        { value: 'distressed', label: 'Distressed Securities', display: 'Distressed Securities' },
        { value: 'credit', label: 'Credit Strategies', display: 'Credit Strategies' },
        { value: 'quantitative', label: 'Quantitative', display: 'Quantitative' },
        { value: 'multi-strategy', label: 'Multi-Strategy', display: 'Multi-Strategy' }
      ],
      experience_levels: [
        { value: '0-2', label: '0-2 years' },
        { value: '3-5', label: '3-5 years' },
        { value: '6-10', label: '6-10 years' },
        { value: '11-15', label: '11-15 years' },
        { value: '16-20', label: '16-20 years' },
        { value: '20+', label: '20+ years' }
      ],
      risk_tolerance: [
        { value: 'conservative', label: 'Conservative' },
        { value: 'moderate', label: 'Moderate' },
        { value: 'aggressive', label: 'Aggressive' },
        { value: 'very-aggressive', label: 'Very Aggressive' }
      ],
      time_horizon: [
        { value: 'short', label: 'Short-term (< 2 years)' },
        { value: 'medium', label: 'Medium-term (2-5 years)' },
        { value: 'long', label: 'Long-term (5-10 years)' },
        { value: 'very-long', label: 'Very Long-term (10+ years)' }
      ],
      net_worth_ranges: [
        { value: '1-5m', label: '$1M - $5M' },
        { value: '5-10m', label: '$5M - $10M' },
        { value: '10-25m', label: '$10M - $25M' },
        { value: '25-50m', label: '$25M - $50M' },
        { value: '50m+', label: '$50M+' }
      ],
      income_ranges: [
        { value: '250k-500k', label: '$250K - $500K' },
        { value: '500k-1m', label: '$500K - $1M' },
        { value: '1m-2m', label: '$1M - $2M' },
        { value: '2m+', label: '$2M+' }
      ],
      institution_types: [
        { value: 'pension-fund', label: 'Pension Fund' },
        { value: 'endowment', label: 'Endowment' },
        { value: 'foundation', label: 'Foundation' },
        { value: 'insurance-company', label: 'Insurance Company' },
        { value: 'family-office', label: 'Family Office' },
        { value: 'fund-of-funds', label: 'Fund of Funds' }
      ],
      aum_ranges: [
        { value: '100m-500m', label: '$100M - $500M' },
        { value: '500m-1b', label: '$500M - $1B' },
        { value: '1b-5b', label: '$1B - $5B' },
        { value: '5b+', label: '$5B+' }
      ],
      communication_preferences: [
        { value: 'email', label: 'Email' },
        { value: 'phone', label: 'Phone' },
        { value: 'video-call', label: 'Video Call' },
        { value: 'in-person', label: 'In-Person' }
      ],
      referral_sources: [
        { value: 'website', label: 'Website Search' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'referral', label: 'Professional Referral' },
        { value: 'conference', label: 'Industry Conference' },
        { value: 'publication', label: 'Industry Publication' },
        { value: 'other', label: 'Other' }
      ],
      departments: [
        { value: 'technology', label: 'Technology' },
        { value: 'operations', label: 'Operations' },
        { value: 'compliance', label: 'Compliance' },
        { value: 'business-development', label: 'Business Development' },
        { value: 'customer-success', label: 'Customer Success' },
        { value: 'finance', label: 'Finance' },
        { value: 'legal', label: 'Legal' }
      ],
      job_titles: [
        { value: 'system-administrator', label: 'System Administrator' },
        { value: 'database-administrator', label: 'Database Administrator' },
        { value: 'operations-manager', label: 'Operations Manager' },
        { value: 'compliance-officer', label: 'Compliance Officer' },
        { value: 'business-analyst', label: 'Business Analyst' },
        { value: 'project-manager', label: 'Project Manager' }
      ]
    };

    return fallbacks[lookupType] || [];
  }

  // Lazy loading methods with memoization
  async getCountries() {
    return this._fetchLookup('countries');
  }

  async getStates() {
    return this._fetchLookup('states');
  }

  async getInvestmentStrategies() {
    return this._fetchLookup('investment_strategies');
  }

  async getExperienceLevels() {
    return this._fetchLookup('experience_levels');
  }

  async getRiskTolerance() {
    return this._fetchLookup('risk_tolerance');
  }

  async getTimeHorizons() {
    return this._fetchLookup('time_horizon');
  }

  async getNetWorthRanges() {
    return this._fetchLookup('net_worth_ranges');
  }

  async getIncomeRanges() {
    return this._fetchLookup('income_ranges');
  }

  async getInstitutionTypes() {
    return this._fetchLookup('institution_types');
  }

  async getAumRanges() {
    return this._fetchLookup('aum_ranges');
  }

  async getCommunicationPreferences() {
    return this._fetchLookup('communication_preferences');
  }

  async getReferralSources() {
    return this._fetchLookup('referral_sources');
  }

  async getDepartments() {
    return this._fetchLookup('departments');
  }

  async getJobTitles() {
    return this._fetchLookup('job_titles');
  }

  async getJurisdictions() {
    const cacheKey = 'jurisdictions_full';
    
    // Check cache first
    const cachedData = this._getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    try {
      const jurisdictionsData = this.getOptions('jurisdictions');
      
      if (jurisdictionsData && Array.isArray(jurisdictionsData)) {
        const hierarchicalData = {
          lookup_type: 'jurisdictions',
          hierarchical: true,
          values: jurisdictionsData.map(jurisdiction => ({
            ...jurisdiction,
            regulatory_bodies: jurisdiction.associations?.regulatory_bodies || []
          })),
          association_metadata: { has_associations: true }
        };
        
        this._setCache(cacheKey, hierarchicalData);
        this._preloadAssociationLookups(jurisdictionsData);
        
        return hierarchicalData;
      }
      
      return { values: [] };
    } catch (error) {
      console.error('Error fetching jurisdictions:', error);
      throw error;
    }
  }

  // Helper method to get just jurisdiction options for dropdown
  async getJurisdictionOptions() {
    const jurisdictionsData = await this.getJurisdictions();
    
    if (jurisdictionsData && Array.isArray(jurisdictionsData.values)) {
      return jurisdictionsData.values.map(jurisdiction => ({
        value: jurisdiction.value,
        label: jurisdiction.display,
        description: jurisdiction.description,
        order: jurisdiction.order || 0
      })).sort((a, b) => a.order - b.order);
    }
    
    return [];
  }

  // Helper method to get full lookup details for simple association values
  async _getFullLookupDetails(lookupType, simpleValues) {
    try {
      // First try to get from cache
      let fullLookupData = this.getOptions(lookupType);
      
      // If not cached, fetch in background but return fallback immediately
      if (!fullLookupData || fullLookupData.length === 0) {
        // Start background fetch (don't await)
        this._fetchLookup(lookupType).catch(error => {
          console.warn(`Background fetch failed for ${lookupType}:`, error);
        });
        
        // Return fallback immediately to prevent delay
        return simpleValues.map(value => ({
          value: value,
          display: value,
          label: value,
          description: '',
          order: 0
        }));
      }
      
      // Use cached data
      return simpleValues.map(value => {
        const fullItem = fullLookupData.find(item => item.value === value);
        return fullItem || {
          value: value,
          display: value,
          label: value,
          description: '',
          order: 0
        };
      });
    } catch (error) {
      console.warn(`Error fetching full details for ${lookupType}:`, error);
      // Fallback to simple format
      return simpleValues.map(value => ({
        value: value,
        display: value,
        label: value,
        description: '',
        order: 0
      }));
    }
  }

  // Synchronous version that returns cached data or fallback immediately
  _getFullLookupDetailsSync(lookupType, simpleValues) {
    try {
      const fullLookupData = this.getOptions(lookupType);
      
      // If no cached data, return fallback and trigger background fetch
      if (!fullLookupData || fullLookupData.length === 0) {
        // Trigger background fetch
        this._fetchLookup(lookupType).catch(error => {
          console.warn(`Background fetch failed for ${lookupType}:`, error);
        });
        
        // Return fallback immediately
        return simpleValues.map(value => ({
          value: value,
          display: value,
          label: value,
          description: '',
          order: 0
        }));
      }
      
      // Use cached data
      return simpleValues.map(value => {
        const fullItem = fullLookupData.find(item => item.value === value);
        return fullItem || {
          value: value,
          display: value,
          label: value,
          description: '',
          order: 0
        };
      });
    } catch (error) {
      console.warn(`Error fetching full details for ${lookupType}:`, error);
      // Fallback to simple format
      return simpleValues.map(value => ({
        value: value,
        display: value,
        label: value,
        description: '',
        order: 0
      }));
    }
  }

  // NEW: Preload association lookup types in background
  _preloadAssociationLookups(jurisdictionValues) {
    const validAssociationTypes = ['regulatory_bodies', 'license_types'];
    const associationTypesToPreload = new Set();
    
    // Only load valid lookup types, not internal fields
    jurisdictionValues.forEach(jurisdiction => {
      if (jurisdiction.associations) {
        Object.keys(jurisdiction.associations)
          .filter(type => validAssociationTypes.includes(type))
          .forEach(associationType => {
            associationTypesToPreload.add(associationType);
          });
      }
    });
    
    // Start background preloading for each association type
    associationTypesToPreload.forEach(lookupType => {
      // Check if already cached
      const cachedData = this.getOptions(lookupType);
      if (!cachedData || cachedData.length === 0) {
        console.log(`Preloading association lookup type: ${lookupType}`);
        this._fetchLookup(lookupType).catch(error => {
          console.warn(`Failed to preload association lookup ${lookupType}:`, error);
        });
      }
    });
    
    // Also preload license_types since regulatory_bodies will need them
    if (associationTypesToPreload.has('regulatory_bodies')) {
      const licenseTypesCache = this.getOptions('license_types');
      if (!licenseTypesCache || licenseTypesCache.length === 0) {
        console.log('Preloading license_types for regulatory bodies');
        this._fetchLookup('license_types').catch(error => {
          console.warn('Failed to preload license_types:', error);
        });
      }
    }
  }

  // Helper method to get regulatory bodies for a jurisdiction
  getRegulatoryBodiesForJurisdiction(jurisdictionsData, jurisdictionValue) {
    if (!jurisdictionsData || !Array.isArray(jurisdictionsData.values)) {
      return [];
    }

    const jurisdiction = jurisdictionsData.values.find(j => j.value === jurisdictionValue);
    if (!jurisdiction) {
      return [];
    }

    // NEW: Handle both old hierarchical format and new association format
    let regulatoryBodies = [];
    
    // Check for old hierarchical format (backward compatibility)
    if (Array.isArray(jurisdiction.regulatory_bodies)) {
      regulatoryBodies = jurisdiction.regulatory_bodies;
    }
    // NEW: Check for new association format
    else if (jurisdiction.associations?.regulatory_bodies) {
      const associatedBodies = jurisdiction.associations.regulatory_bodies;
      
      // Handle both simple (string array) and complex (object array) formats
      if (associatedBodies.length > 0 && typeof associatedBodies[0] === 'string') {
        // Simple format: ["sec", "finra"] - need to fetch full details
        regulatoryBodies = this._getFullLookupDetailsSync('regulatory_bodies', associatedBodies);
      } else {
        // Complex format: [{value: "sec", display: "SEC"...}] - use as is
        regulatoryBodies = associatedBodies;
      }
    }

    return regulatoryBodies.map(body => ({
      value: body.value,
      display: body.display,
      label: body.display, // Backward compatibility
      description: body.description,
      verification_link: body.verification_link,
      notes: body.notes,
      order: body.order || 0,
      // NEW: Include associations if available
      associations: body.associations || {}
    })).sort((a, b) => a.order - b.order);
  }

  // Helper method to get license types for a regulatory body
  getLicenseTypesForRegulatory(jurisdictionsData, jurisdictionValue, regulatoryBodyValue) {
    if (!jurisdictionsData || !Array.isArray(jurisdictionsData.values)) {
      return [];
    }

    const jurisdiction = jurisdictionsData.values.find(j => j.value === jurisdictionValue);
    if (!jurisdiction) {
      return [];
    }

    // NEW: Handle both old hierarchical format and new association format
    let regulatoryBodies = [];
    
    // Check for old hierarchical format (backward compatibility)
    if (Array.isArray(jurisdiction.regulatory_bodies)) {
      regulatoryBodies = jurisdiction.regulatory_bodies;
    }
    // NEW: Check for new association format
    else if (jurisdiction.associations?.regulatory_bodies) {
      regulatoryBodies = jurisdiction.associations.regulatory_bodies;
    }

    const regulatoryBody = regulatoryBodies.find(rb => rb.value === regulatoryBodyValue);
    if (!regulatoryBody) {
      return [];
    }

    // NEW: Handle both old hierarchical format and new association format for license types
    let licenseTypes = [];
    
    // Check for old hierarchical format (backward compatibility)
    if (Array.isArray(regulatoryBody.license_types)) {
      licenseTypes = regulatoryBody.license_types;
    }
    // NEW: Check for new association format
    else if (regulatoryBody.associations?.license_types) {
      const associatedLicenses = regulatoryBody.associations.license_types;
      
      // Handle both simple (string array) and complex (object array) formats
      if (associatedLicenses.length > 0 && typeof associatedLicenses[0] === 'string') {
        // Simple format: ["sec_number"] - need to fetch full details
        licenseTypes = this._getFullLookupDetailsSync('license_types', associatedLicenses);
      } else {
        // Complex format: [{value: "sec_number", display: "SEC Number"...}] - use as is
        licenseTypes = associatedLicenses;
      }
    }

    return licenseTypes.map(license => ({
      value: license.value,
      display: license.display,
      label: license.display, // Backward compatibility
      description: license.description,
      example_format: license.example_format,
      order: license.order || 0
    })).sort((a, b) => a.order - b.order);
  }

  // Helper method to find jurisdiction by country
  findJurisdictionByCountry(jurisdictionsData, countryValue) {
    if (!jurisdictionsData || !Array.isArray(jurisdictionsData.values)) {
      return null;
    }

    // Direct mapping based on common patterns
    const countryToJurisdictionMap = {
      'United States': 'united_states',
      'US': 'united_states',
      'USA': 'united_states',
      'United Kingdom': 'united_kingdom',
      'UK': 'united_kingdom',
      'GB': 'united_kingdom',
      'Canada': 'canada',
      'CA': 'canada',
      'Germany': 'germany',
      'DE': 'germany',
      'France': 'france',
      'FR': 'france',
      'Switzerland': 'switzerland',
      'CH': 'switzerland',
      'Luxembourg': 'luxembourg',
      'LU': 'luxembourg'
    };

    const jurisdictionValue = countryToJurisdictionMap[countryValue];
    if (jurisdictionValue) {
      return jurisdictionsData.values.find(j => j.value === jurisdictionValue);
    }

    // Fallback: try to find by display name similarity
    return jurisdictionsData.values.find(j => 
      j.display.toLowerCase().includes(countryValue.toLowerCase()) ||
      countryValue.toLowerCase().includes(j.display.toLowerCase())
    );
  }

  // Cache management utilities
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
    // Reset initialization state so lookups can be reloaded
    this.isInitialized = false;
    this.initializationPromise = null;
  }

  getCacheSize() {
    return this.cache.size;
  }

  getCacheInfo() {
    const info = {};
    for (const [key, entry] of this.cache.entries()) {
      info[key] = {
        size: entry.data.length,
        age: Date.now() - entry.timestamp,
        valid: this._isCacheValid(entry),
        sessionPersistent: true // Cache persists for entire session
      };
    }
    return info;
  }

  // NEW: Initialize ALL lookups for the session
  async initializeAllLookups() {
    if (this.isInitialized) {
      return this.cache;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._performFullInitialization();
    return this.initializationPromise;
  }

  async _performFullInitialization() {
    try {
      const response = await api.get('/fetch/lookups', { 
        params: { lookup_type: 'all' } 
      });
      
      if (response.data && response.data.bulk && response.data.lookup_types) {
        const lookupTypes = response.data.lookup_types;
        
        // Process and cache all lookup types
        for (const [lookupType, lookupData] of Object.entries(lookupTypes)) {
          if (lookupData.error) {
            console.warn(`Lookup type '${lookupType}' had error: ${lookupData.error}`);
            // Cache empty array for errored lookups
            this._setCache(this._getCacheKey(lookupType), []);
            continue;
          }
          
          const values = lookupData.values || [];
          
          const formattedData = values.map(normalizeLookupItem).filter(Boolean);
          
          // Cache the formatted data
          const cacheKey = this._getCacheKey(lookupType);
          this._setCache(cacheKey, formattedData);
          
          // NEW: Special handling for jurisdictions to maintain backward compatibility
          if (lookupType === 'jurisdictions' && lookupData.association_metadata?.has_associations) {
            const hierarchicalData = {
              lookup_type: 'jurisdictions',
              hierarchical: true,
              values: formattedData.map(jurisdiction => ({
                ...jurisdiction,
                // Extract regulatory bodies from associations for backward compatibility
                regulatory_bodies: jurisdiction.associations?.regulatory_bodies || []
              })),
              association_metadata: lookupData.association_metadata,
              source: lookupData.source
            };
            this._setCache(`${this._getCacheKey(lookupType)}_full`, hierarchicalData);
          }
        }
        
        this.isInitialized = true;
        return this.cache;
      } else {
        console.error('Invalid bulk response format from lookup API');
        throw new Error('Invalid bulk response format from lookup API');
      }
    } catch (error) {
      console.error('Failed to initialize lookups:', error);
      
      // Don't mark as initialized on error
      this.initializationPromise = null;
      throw error;
    }
  }

  // NEW: Get display value for any lookup type and value
  getDisplayValue(lookupType, value, fallback = null) {
    if (!value) return fallback;
    
    // Handle arrays (multi-select values) - return first value or formatted string
    if (Array.isArray(value)) {
      if (value.length === 0) return fallback;
      if (value.length === 1) {
        // Single value in array, process normally
        return this.getDisplayValue(lookupType, value[0], fallback);
      }
      // Multiple values - return formatted string
      const displayValues = value.map(v => this.getDisplayValue(lookupType, v, v));
      return displayValues.join(', ');
    }
    
    // Ensure value is a string for toLowerCase operations
    if (typeof value !== 'string') {
      return fallback || String(value);
    }
    
    const options = this.getOptions(lookupType);
    
    if (!options || options.length === 0) {
      return fallback || value;
    }
    
    // Enhanced matching: check both lookup values AND display values
    const option = options.find(opt => 
      // Match against lookup values (existing logic)
      opt.value === value || 
      (typeof opt.value === 'string' && opt.value.toLowerCase() === value.toLowerCase()) ||
      // Match against display values (new logic for AI extraction)
      opt.label === value ||
      (typeof opt.label === 'string' && opt.label.toLowerCase() === value.toLowerCase()) ||
      opt.display === value ||
      (typeof opt.display === 'string' && opt.display.toLowerCase() === value.toLowerCase())
    );
    
    if (!option) {
      // Silently handle missing lookup values - only log if value is not empty
      if (value && value.trim() !== '') {
        console.warn(`[LOOKUP] No match for type: ${lookupType}, value: ${value}`);
      }
      return fallback || value;
    }
    
    return option.label || option.display || option.value;
  }

  /**
   * Resolve a human-entered or AI string (or existing id) to the canonical
   * lookup `value` stored in forms — same matching rules as getDisplayValue,
   * but returns option.value instead of label.
   *
   * @param {string} lookupType
   * @param {string|string[]|null|undefined} value
   * @param {string|null} fallback when no match (null = return null)
   * @returns {string|null}
   */
  resolveLookupFormValue(lookupType, value, fallback = null) {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return fallback;
      return this.resolveLookupFormValue(lookupType, value[0], fallback);
    }

    if (typeof value !== 'number' && typeof value !== 'string') {
      return fallback;
    }

    const strVal = String(value).trim();
    if (!strVal) return fallback;

    const options = this.getOptions(lookupType);
    if (!options || options.length === 0) {
      return fallback !== null && fallback !== undefined ? fallback : null;
    }

    const option = options.find(
      (opt) =>
        opt.value === strVal ||
        (typeof opt.value === 'string' && opt.value.toLowerCase() === strVal.toLowerCase()) ||
        opt.label === strVal ||
        (typeof opt.label === 'string' && opt.label.toLowerCase() === strVal.toLowerCase()) ||
        (opt.display !== undefined &&
          (opt.display === strVal ||
            (typeof opt.display === 'string' && opt.display.toLowerCase() === strVal.toLowerCase())))
    );

    if (!option) {
      if (strVal) {
        console.warn(`[LOOKUP] resolveLookupFormValue no match: ${lookupType}, value: ${strVal}`);
      }
      return fallback !== null && fallback !== undefined ? fallback : null;
    }

    return option.value;
  }

  /**
   * Map wizard/import legacy redemption frequency ids to canonical lookup values.
   * Handles records saved before the opportunity wizard used redemption_frequencies.
   */
  normalizeRedemptionFrequency(value, fallback = 'quarterly') {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    const legacyAliases = {
      'semi-annual': 'semi_annually',
      semi_annual: 'semi_annually',
      annual: 'annually',
      'bi-weekly': 'weekly',
      bi_weekly: 'weekly',
    };

    const strVal = String(value).trim();
    const lower = strVal.toLowerCase();
    const aliased = legacyAliases[lower] ?? lower.replace(/-/g, '_');

    const resolved = this.resolveLookupFormValue('redemption_frequencies', aliased, null);
    if (resolved) {
      return resolved;
    }

    const canonicalIds = new Set([
      'daily',
      'weekly',
      'monthly',
      'quarterly',
      'semi_annually',
      'annually',
      'other',
    ]);
    if (canonicalIds.has(aliased)) {
      return aliased;
    }

    return fallback;
  }

  // NEW: Get color for any lookup type and value  
  getColor(lookupType, value, fallback = '#757575') {
    if (!lookupType || !value) return fallback;
    
    // Handle arrays (multi-select values) - return color for first value
    if (Array.isArray(value)) {
      if (value.length === 0) return fallback;
      // Use first value for color
      return this.getColor(lookupType, value[0], fallback);
    }
    
    // Ensure value is a string for toLowerCase operations
    if (typeof value !== 'string') {
      return fallback;
    }
    
    const cacheKey = this._getCacheKey(lookupType);
    const lookupData = this._getFromCache(cacheKey);
    
    if (!lookupData) return fallback;
    
    const item = lookupData.find(item => 
      item.value === value || 
      (typeof item.value === 'string' && item.value.toLowerCase() === value.toLowerCase())
    );
    
    return item?.color || fallback;
  }

  // NEW: Get all attributes for a lookup value
  getLookupAttributes(lookupType, value) {
    if (!lookupType || !value) return null;
    
    // Handle arrays (multi-select values) - return attributes for first value
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      return this.getLookupAttributes(lookupType, value[0]);
    }
    
    // Ensure value is a string for toLowerCase operations
    if (typeof value !== 'string') {
      return null;
    }
    
    const cacheKey = this._getCacheKey(lookupType);
    const lookupData = this._getFromCache(cacheKey);
    
    if (!lookupData) return null;
    
    const item = lookupData.find(item => 
      item.value === value || 
      (typeof item.value === 'string' && item.value.toLowerCase() === value.toLowerCase())
    );
    
    // NEW: Return item with associations included
    return item || null;
  }

  // NEW: Get options for dropdowns/selects
  getOptions(lookupType, includeEmpty = false) {
    const cacheKey = this._getCacheKey(lookupType);
    let cachedData = this._getFromCache(cacheKey);
    
    // If no cached data, log warning and return empty array
    if (!cachedData || cachedData.length === 0) {
      console.warn(`No cached data found for lookup type: ${lookupType}`);
      return [];
    }
    
    let options = cachedData
      .filter(item => item.active !== false)
      .sort((a, b) => a.order - b.order)
      .map(item => ({
        value: item.value,
        display: item.display || item.label || item.value,
        label: item.display || item.label || item.value,
        color: item.color,
        description: item.description
      }));
    
    if (includeEmpty) {
      options.unshift({ value: '', label: '-- Select --' });
    }
    
    return options;
  }

  // NEW: Check if lookups are ready
  isReady() {
    return this.isInitialized;
  }

  // NEW: Wait for initialization to complete
  async waitForInitialization() {
    if (this.isInitialized) return true;
    
    try {
      await this.initializeAllLookups();
      return true;
    } catch (error) {
      console.error('Failed to wait for lookup initialization:', error);
      return false;
    }
  }

  // NEW: Get associated values for a lookup item
  getAssociatedValues(lookupType, value, associationType) {
    const attributes = this.getLookupAttributes(lookupType, value);
    if (!attributes || !attributes.associations) {
      return [];
    }
    
    return attributes.associations[associationType] || [];
  }

  // NEW: Simplified method to get regulatory bodies for a jurisdiction (using associations)
  getRegulatoryBodiesForJurisdictionSimple(jurisdictionValue) {
    return this.getAssociatedValues('jurisdictions', jurisdictionValue, 'regulatory_bodies');
  }

  // NEW: Simplified method to get license types for a regulatory body (using associations)
  getLicenseTypesForRegulatorySimple(regulatoryBodyValue) {
    return this.getAssociatedValues('regulatory_bodies', regulatoryBodyValue, 'license_types');
  }

  /**
   * Populate singleton cache from the curator workspace `lookups.json` snapshot
   * (?format=structured). Fills only cache keys that are still empty so live
   * `/fetch/lookups` data wins when it loaded successfully (admin Data Tool review).
   *
   * @param {object} structured — same shape as backend build_structured_format()
   */
  hydrateFromCuratorStructured(structured) {
    if (!structured || typeof structured !== 'object') {
      return;
    }

    const toUniform = (rows) => (Array.isArray(rows) ? rows : []).map(({ id, label }) => ({
      value: id,
      display: label || id,
      label: label || id,
      active: true,
      order: 0,
    }));

    const sfByAt = structured.strategy_focus_by_asset_type || {};
    const assetRows = (structured.asset_types || []).map(({ id, label }) => {
      const key = (id || '').toLowerCase();
      const sfVals = sfByAt[key] || [];
      return {
        value: id,
        display: label || id,
        label: label || id,
        active: true,
        order: 0,
        associations: { strategy_focus: sfVals },
      };
    });

    const sfRows = (structured.strategy_focus || []).map(({ id, label, asset_type: at }) => ({
      value: id,
      display: label || id,
      label: label || id,
      active: true,
      order: 0,
      associations: { asset_type: at ? [at] : [] },
    }));

    const mergeKey = (lookupType, formatted) => {
      if (!formatted.length) return;
      const cacheKey = this._getCacheKey(lookupType);
      const cur = this._getFromCache(cacheKey);
      if (cur && cur.length > 0) return;
      this._setCache(cacheKey, formatted);
    };

    mergeKey('asset_type', assetRows);
    mergeKey('strategy_focus', sfRows);
    mergeKey('regions', toUniform(structured.regions));
    mergeKey('sectors', toUniform(structured.sectors));
    mergeKey('countries', toUniform(structured.countries));
    mergeKey('jurisdictions', toUniform(structured.domiciles));
    mergeKey('co_invest_structure', toUniform(structured.structures));
    mergeKey('co_invest_deal_stage', toUniform(structured.deal_stages));
    mergeKey('redemption_frequencies', toUniform(structured.redemption_frequencies));
    mergeKey('job_titles', toUniform(structured.job_titles));
  }

  // NEW: Get strategy focus options for an asset type
  getStrategyFocusForAssetType(assetTypeValue) {
    return this.getAssociatedValues('asset_type', assetTypeValue, 'strategy_focus');
  }

  // NEW: Get asset type for a strategy focus
  getAssetTypeForStrategyFocus(strategyFocusValue) {
    return this.getAssociatedValues('strategy_focus', strategyFocusValue, 'asset_type');
  }
}

// Create singleton instance
const lookupService = new LookupService();

// Initialize all lookups when the service is imported
export const initializeAllLookups = () => {
  return lookupService.initializeAllLookups();
};

// Utility function to ensure lookups are ready before using
export const withLookups = async (callback) => {
  await lookupService.waitForInitialization();
  return callback(lookupService);
};

// Export both the service and utility functions
export { lookupService as default };
export { lookupService }; 